/**
 * The pure half of lib/propertyOutreach.ts (Contact the property; Warren, 4 Nov 2026, ruling R10): the draft's shape and the
 * rules the card and the page use, free of the Api client so the tests can load them. The Api checks everything again: To
 * may only hold the location's contacts, and only an EVI person's press sends.
 */
export type OutreachSourceKind = 'alert' | 'service_request' | 'breaker_step';
export type OutreachStatus = 'draft' | 'sending' | 'sent' | 'discarded';
export type OutreachResult = 'sent' | 'held' | 'failed' | 'unknown';

export interface OutreachView {
  id: string;
  source_kind: OutreachSourceKind;
  source_id: string;
  purpose: 'ask' | 'still_offline' | 'still_faulted' | 'thanks' | null;
  status: OutreachStatus;
  to: string[];
  subject: string;
  body: string;
  location: { id: string; name: string };
  /** The location's contacts: the only addresses a draft may hold. */
  contacts: Array<{ name: string; email: string }>;
  /** The breaker step: each person's own Done link is added to their copy at send. */
  done_link_appended: boolean;
  warning: string | null;
  /** redraft: the breaker step's draft again, for whoever its ask has not reached (dropped, or it reached only some). */
  can: { edit: boolean; send: boolean; discard: boolean; redraft?: boolean };
  sent_by: string | null;
  sent_at: string | null;
  results: Array<{ email: string; result: OutreachResult; code: string | null; at: string }>;
  created_at: string;
  updated_at: string;
}

export interface OutreachEdits { to: string[]; subject: string; body: string }

export const editsOf = (d: Pick<OutreachView, 'to' | 'subject' | 'body'>): OutreachEdits => ({ to: [...d.to], subject: d.subject, body: d.body });

const sameAddress = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();
export const isOnTo = (to: readonly string[], email: string) => to.some((x) => sameAddress(x, email));

/** Ticks or unticks one contact. EVI may take people off and put them back; the list only ever holds contacts. */
export function toggleRecipient(to: readonly string[], email: string, on: boolean): string[] {
  const rest = to.filter((x) => !sameAddress(x, email));
  return on ? [...rest, email] : rest;
}

/** What the person changed, for the PATCH. Empty: nothing to save. */
export function changedFields(d: Pick<OutreachView, 'to' | 'subject' | 'body'>, e: OutreachEdits): Partial<OutreachEdits> {
  const out: Partial<OutreachEdits> = {};
  if (e.subject !== d.subject) out.subject = e.subject;
  if (e.body !== d.body) out.body = e.body;
  const key = (list: readonly string[]) => list.map((x) => x.trim().toLowerCase()).sort().join('\n');
  if (key(e.to) !== key(d.to)) out.to = e.to;
  return out;
}

export const sendLabel = (n: number) => (n === 1 ? 'Send to 1 person' : `Send to ${n} people`);

/** The press on a breaker step's draft that was dropped or reached only some of its people. */
export const redraftLabel = (d: Pick<OutreachView, 'status'>) => (d.status === 'discarded' ? 'Draft it again' : 'Draft it again for whoever it did not reach');

const RESULT_LABEL: Readonly<Record<OutreachResult, string>> = {
  sent: 'Sent',
  held: 'Held: this is not production, so it did not go',
  failed: 'Did not go',
  unknown: 'Not confirmed: it may have gone',
};
export const resultLabel = (r: OutreachResult) => RESULT_LABEL[r];

/** One line for a draft that is no longer open. */
export function outcomeLine(d: Pick<OutreachView, 'status' | 'sent_at' | 'to'>, when: (iso: string) => string): string {
  if (d.status === 'discarded') return 'Dropped. Nothing went out.';
  if (d.status === 'sending') return 'Sending now.';
  if (d.status === 'sent') return `Sent ${d.sent_at ? when(d.sent_at) : ''} to ${d.to.length === 1 ? '1 person' : `${d.to.length} people`}.`.replace(/ {2,}/g, ' ');
  return 'A draft. Nothing has gone out.';
}

/** Where a draft came from, for the link back. The breaker step's lives in the queue. */
export function sourcePath(d: Pick<OutreachView, 'source_kind' | 'source_id'>): { label: string; path: string } {
  if (d.source_kind === 'alert') return { label: 'The alert', path: `/alerts/${d.source_id}` };
  if (d.source_kind === 'service_request') return { label: 'The service call', path: `/service-requests/${d.source_id}` };
  return { label: 'EVI queues', path: '/evi-queues#property_contact' };
}
